import { computed } from 'vue'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useGlobalStore }           from 'stores/global.js'

// Single source of truth for "what pixel is what time" across the time-grid
// views (week, day, 4-day). These numbers used to be redeclared in every view
// and in CalendarEventCard, so a change to one silently desynced the others.

export const ROW_HEIGHT        = 64   // px per hour
export const GRID_HEIGHT       = ROW_HEIGHT * 24
export const SNAP_MINUTES      = 15   // drag/resize granularity
export const MIN_EVENT_MINUTES = 15   // shortest event a drag can produce
export const MIN_CARD_HEIGHT   = 18   // px — a 15-minute chip still needs to be readable

// Intl.DateTimeFormat construction is expensive and these are called per
// booking per render, so every formatter is built once and reused.
const _formatterCache = new Map()

export function formatter(timeZone, options) {
  const key = `${timeZone}|${JSON.stringify(options)}`
  let fmt = _formatterCache.get(key)
  if (!fmt) {
    try {
      fmt = new Intl.DateTimeFormat(options.locale ?? 'en-US', { timeZone, ...options })
    } catch {
      fmt = new Intl.DateTimeFormat(options.locale ?? 'en-US', options)
    }
    _formatterCache.set(key, fmt)
  }
  return fmt
}

export function browserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return 'UTC' }
}

// ── Colour helpers ────────────────────────────────────────────────────────────

export function darkenColor(hex, factor = 0.15) {
  const { r, g, b } = parseHex(hex)
  return `rgb(${Math.round(r * (1 - factor))},${Math.round(g * (1 - factor))},${Math.round(b * (1 - factor))})`
}

function parseHex(hex) {
  const h = String(hex ?? '').replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const n = parseInt(full, 16)
  if (Number.isNaN(n)) return { r: 59, g: 130, b: 246 } // fall back to blue-500
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff }
}

// Chip text used to be hardcoded white, which is unreadable on the lighter
// palette colours (banana, flamingo). Pick whichever of black/white has more
// contrast against the fill, per WCAG relative luminance.
export function readableTextColor(hex) {
  const { r, g, b } = parseHex(hex)
  const channel = (c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
  return luminance > 0.45 ? '#1f2937' : '#ffffff'
}

export function useCalendarGeometry() {
  const settingsStore = useCalendarSettingsStore()
  const globalStore   = useGlobalStore()

  const primaryTz = computed(() =>
    settingsStore.date_primary_timezone || browserTimezone()
  )

  const secondaryTz = computed(() => settingsStore.date_secondary_timezone || null)

  const showSecondary = computed(() =>
    settingsStore.display_secondary_time_zone && !!secondaryTz.value
  )

  const is24Hour = computed(() => globalStore.user?.time_format === '24_hours')

  // ── Date → grid position ───────────────────────────────────────────────────

  // 'YYYY-MM-DD' for the given instant, as seen in the primary timezone. This
  // is the identity of a day column.
  function dayKey(date) {
    try { return formatter(primaryTz.value, { locale: 'en-CA' }).format(date) }
    catch { return new Date(date).toISOString().split('T')[0] }
  }

  // Minutes since midnight for the given instant, in the primary timezone.
  function minutesOfDay(date) {
    try {
      const parts = formatter(primaryTz.value, {
        hour: 'numeric', minute: 'numeric', hour12: false,
      }).formatToParts(date)
      const h = parseInt(parts.find(p => p.type === 'hour').value, 10) % 24
      const m = parseInt(parts.find(p => p.type === 'minute').value, 10)
      return h * 60 + m
    } catch {
      const d = new Date(date)
      return d.getHours() * 60 + d.getMinutes()
    }
  }

  const minutesToY = (minutes) => (minutes / 60) * ROW_HEIGHT
  const timeToY    = (date)    => minutesToY(minutesOfDay(date))

  // Inverse: a pixel offset inside a day column back to minutes since midnight.
  function yToMinutes(y) {
    return clampMinutes(Math.round((y / ROW_HEIGHT) * 60))
  }

  const clampMinutes = (m) => Math.max(0, Math.min(24 * 60, m))
  const snapMinutes  = (m) => clampMinutes(Math.round(m / SNAP_MINUTES) * SNAP_MINUTES)

  // Builds a concrete Date for "this many minutes into this day column".
  // dayMidnight is a local Date at 00:00, matching how the views build columns.
  function dateAtMinutes(dayMidnight, minutes) {
    const d = new Date(dayMidnight)
    d.setHours(0, 0, 0, 0)
    d.setMinutes(minutes)
    return d
  }

  function currentMinutesOfDay() {
    return minutesOfDay(new Date())
  }

  // ── Labels ─────────────────────────────────────────────────────────────────

  function gmtOffset(tz) {
    try {
      const parts = formatter(tz, { locale: 'en', timeZoneName: 'shortOffset' }).formatToParts(new Date())
      const raw = parts.find(p => p.type === 'timeZoneName')?.value ?? ''
      return raw.replace(/GMT([+-])(\d)(?::(\d{2}))?$/, (_, sign, h, m) =>
        `GMT${sign}${h.padStart(2, '0')}${m ? ':' + m : ''}`)
    } catch { return '' }
  }

  const primaryTzLabel = computed(() =>
    settingsStore.formatted_label_for_primary_timezone || gmtOffset(primaryTz.value)
  )

  const secondaryTzLabel = computed(() =>
    settingsStore.formatted_label_for_secondary_timezone ||
    (secondaryTz.value ? gmtOffset(secondaryTz.value) : '')
  )

  const hourLabels = computed(() =>
    Array.from({ length: 24 }, (_, h) => {
      if (is24Hour.value) return `${String(h).padStart(2, '0')}:00`
      return new Date(2000, 0, 1, h).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })
    })
  )

  // The instant at which "today" began in the primary timezone — the anchor the
  // secondary-timezone labels are offset from.
  function primaryMidnightUTC() {
    const dateStr = formatter(primaryTz.value, { locale: 'en-CA' }).format(new Date())
    const utcMid  = new Date(`${dateStr}T00:00:00Z`)
    const parts   = formatter(primaryTz.value, { hour: 'numeric', minute: 'numeric', hour12: false })
      .formatToParts(utcMid)
    const hOff = parseInt(parts.find(p => p.type === 'hour').value, 10) % 24
    const mOff = parseInt(parts.find(p => p.type === 'minute').value, 10)
    return new Date(utcMid.getTime() - (hOff * 60 + mOff) * 60000)
  }

  const secondaryHourLabels = computed(() => {
    if (!showSecondary.value) return []
    const tz = secondaryTz.value
    try {
      const midnight = primaryMidnightUTC()
      return Array.from({ length: 24 }, (_, h) => {
        const t = new Date(midnight.getTime() + h * 3600000)
        if (!is24Hour.value) {
          return formatter(tz, { hour: 'numeric', hour12: true }).format(t)
        }
        const p = formatter(tz, { hour: 'numeric', hour12: false }).formatToParts(t)
        return `${String(parseInt(p.find(x => x.type === 'hour').value, 10) % 24).padStart(2, '0')}:00`
      })
    } catch {
      return Array(24).fill('')
    }
  })

  function formatTime(date) {
    return formatter(primaryTz.value, {
      hour: 'numeric', minute: '2-digit', hour12: !is24Hour.value,
    }).format(new Date(date))
  }

  return {
    ROW_HEIGHT, GRID_HEIGHT, SNAP_MINUTES, MIN_EVENT_MINUTES, MIN_CARD_HEIGHT,
    primaryTz, secondaryTz, showSecondary, is24Hour,
    dayKey, minutesOfDay, minutesToY, timeToY, yToMinutes, snapMinutes, clampMinutes,
    dateAtMinutes, currentMinutesOfDay,
    primaryTzLabel, secondaryTzLabel, hourLabels, secondaryHourLabels,
    formatTime, darkenColor, readableTextColor,
  }
}
